<template>
  <!--
    O diálogo ÚNICO do sistema — confirmar, avisar e perguntar —, alimentado pelo
    `dialogo.service.ts`. Montado uma vez no App.vue, fora do layout, para funcionar em qualquer
    tela (inclusive as públicas) e acima de qualquer outro modal: é por isso o z-index alto (o modal
    de relatório usa 3000).
  -->
  <ModalConsulte
    ref="modal"
    modal-width="480px"
    z-index="3100 !important"
    @onClose="aoFechar"
  >
    <template #header>
      <h2 class="modal-title h5 d-flex align-items-center gap-2">
        <i :class="icone" aria-hidden="true"></i>
        {{ pedido?.titulo }}
      </h2>
    </template>

    <template #content>
      <form v-if="pedido" :id="idFormulario" novalidate @submit.prevent="confirmar">
        <!-- pre-line: a mensagem pode ter quebras de linha. NUNCA v-html — o texto pode vir de
             dado de usuário. -->
        <p :id="`${idBase}-mensagem`" class="dialogo-sistema__mensagem mb-0">{{ pedido.mensagem }}</p>

        <div v-if="pedido.tipo === 'perguntar'" class="mt-3">
          <label v-if="pedido.rotulo" class="form-label" :for="`${idBase}-campo`">{{ pedido.rotulo }}</label>
          <input
            :id="`${idBase}-campo`"
            ref="campo"
            v-model="valor"
            :type="pedido.tipoCampo"
            class="form-control"
            :class="{ 'is-invalid': erro }"
            :required="pedido.obrigatorio"
            :autocomplete="pedido.tipoCampo === 'password' ? 'current-password' : 'off'"
            :aria-labelledby="pedido.rotulo ? undefined : `${idBase}-mensagem`"
            :aria-invalid="erro ? 'true' : undefined"
            :aria-describedby="erro ? `${idBase}-erro` : undefined"
          />
          <div v-if="erro" :id="`${idBase}-erro`" class="invalid-feedback d-block" role="alert">{{ erro }}</div>
        </div>
      </form>
    </template>

    <template #actions>
      <template v-if="pedido">
        <!-- Cancelar vem PRIMEIRO na ordem: numa ação destrutiva, Enter/Tab não caem direto no
             botão que apaga. -->
        <button
          v-if="pedido.tipo !== 'avisar'"
          type="button"
          class="btn btn-light"
          @click="cancelar"
        >
          {{ pedido.textoCancelar }}
        </button>
        <button
          :type="pedido.tipo === 'perguntar' ? 'submit' : 'button'"
          :form="pedido.tipo === 'perguntar' ? idFormulario : undefined"
          class="btn"
          :class="pedido.perigo ? 'btn-danger' : 'btn-primary'"
          @click="pedido.tipo === 'perguntar' ? undefined : confirmar()"
        >
          {{ pedido.textoConfirmar }}
        </button>
      </template>
    </template>
  </ModalConsulte>
</template>

<script lang="ts">
  import { defineComponent } from 'vue';

  import ModalConsulte from '@/components/modal/ModalConsulte.vue';
  import { estadoDialogo, responderDialogo } from '@/services/globals/dialogo.service';

  /**
   * O substituto de `window.confirm/alert/prompt` — ver `dialogo.service.ts`. As telas NÃO usam
   * este componente direto: chamam o serviço, que devolve uma promessa.
   */
  export default defineComponent({
    name: 'DialogoSistema',
    components: { ModalConsulte },
    data() {
      return {
        estado: estadoDialogo,
        valor: '',
        erro: '',
        /** O fechamento que o próprio componente provoca não é "cancelar". */
        respondido: false,
        idBase: `dialogo-${Math.random().toString(36).slice(2, 8)}`,
      };
    },
    computed: {
      pedido() {
        return this.estado.atual;
      },
      idFormulario(): string {
        return `${this.idBase}-form`;
      },
      icone(): string {
        const tom = this.pedido?.perigo ? 'perigo' : this.pedido?.tom;

        switch (tom) {
          case 'perigo':
            return 'mdi mdi-alert-octagon-outline text-danger';
          case 'erro':
            return 'mdi mdi-close-circle-outline text-danger';
          case 'aviso':
            return 'mdi mdi-alert-outline text-warning';
          case 'sucesso':
            return 'mdi mdi-check-circle-outline text-success';
          default:
            return 'mdi mdi-information-outline text-info';
        }
      },
    },
    watch: {
      pedido: {
        immediate: true,
        handler(novo) {
          if (!novo) return;

          this.valor = novo.valor ?? '';
          this.erro = '';
          this.respondido = false;

          // Na fila, o próximo chega no mesmo tique em que o anterior fecha: o nextTick deixa o
          // ModalConsulte terminar de fechar antes de abrir de novo.
          this.$nextTick(() => {
            (this.$refs.modal as any)?.open();

            if (novo.tipo === 'perguntar') {
              this.$nextTick(() => (this.$refs.campo as HTMLInputElement | undefined)?.focus());
            }
          });
        },
      },
    },
    methods: {
      confirmar() {
        const pedido = this.pedido;

        if (!pedido) return;

        if (pedido.tipo === 'perguntar') {
          const texto = this.valor ?? '';

          if (pedido.obrigatorio && !texto.trim()) {
            this.erro = pedido.rotulo ? `Preencha ${pedido.rotulo.toLowerCase()}.` : 'Preencha o campo.';
            (this.$refs.campo as HTMLInputElement | undefined)?.focus();
            return;
          }

          const problema = pedido.validar?.(texto);

          if (problema) {
            this.erro = problema;
            (this.$refs.campo as HTMLInputElement | undefined)?.focus();
            return;
          }

          this.responder(texto);
          return;
        }

        this.responder(true);
      },
      cancelar() {
        this.responder(this.pedido?.tipo === 'perguntar' ? null : false);
      },
      /** Esc ou X: vale como cancelar (no aviso, como "entendi"). */
      aoFechar() {
        if (this.respondido) return;

        this.respondido = true;
        responderDialogo(this.pedido?.tipo === 'avisar' ? undefined : this.pedido?.tipo === 'perguntar' ? null : false);
      },
      responder(resposta: unknown) {
        this.respondido = true;
        (this.$refs.modal as any)?.close();
        responderDialogo(resposta);
      },
    },
  });
</script>

<style scoped>
  .dialogo-sistema__mensagem {
    white-space: pre-line;
  }
</style>
